import { CommonModule } from '@angular/common';
import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  Input,
  inject,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  FormArray,
  AbstractControl,
} from '@angular/forms';
import { ProjectsService } from '@/app/services/projects/projects.service';
import { NgToastService } from 'ng-angular-popup';
import { MatIconModule } from '@angular/material/icon';
import { StakeholderComponent } from '@/app/components/projects/project-stages/project-brief/stakeholder/stakeholder.component';
import { GradeComponent } from '../../grade/grade.component';
import { NgxCurrencyDirective } from 'ngx-currency';
import { CountryService } from '@/app/services/shared/country';
import { WordConverterService } from '@/app/services/shared/word-converter.service';
import { VIEW_MODE } from '@/app/interfaces/view-mode.enum';
import { ProgressService } from '@/app/services/shared/progress.service';
import { SendForApprovalComponent } from '@/app/components/global/dialog-boxes/send-for-approval/send-for-approval.component';
import { MatDialog } from '@angular/material/dialog';
import { TabbedScrollerComponent } from '../../../../tabbed-scroller/tabbed-scroller.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { provideNativeDateAdapter } from '@angular/material/core';
import { Router } from '@angular/router';
import { GetProjectIdService } from '@/app/services/getProjectId.service';
import { DialogUtilityService } from '@/app/services/utility/dialog-utility.service';
import { ProgressBarComponent } from '../../../../progress-bar/progress-bar.component';
enum PROJECT_MESSAGES {
  PROJECT_CREATED = 'Project created successfully',
  PROJECT_UPDATED = 'Project updated successfully',
  REQUIRED_FIELDS_MISSING = 'Please fill all the required fields',
  ADDRESS_LOAD_FAILED = 'Failed to load address details',
}

interface ApprovalDetail {
  feedback: string | null;
  status: string;
  userName: string;
  userTitle: string | null;
  parentStatus: string;
}

@Component({
  standalone: true,
  providers: [provideNativeDateAdapter()],
  imports: [
    FormsModule,
    CommonModule,
    ReactiveFormsModule,
    MatIconModule,
    StakeholderComponent,
    GradeComponent,
    NgxCurrencyDirective,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    ProgressBarComponent,
  ],
  selector: 'app-budget-and-timeline',
  templateUrl: './budget-and-timeline.component.html',
  styleUrls: ['./budget-and-timeline.component.css'],
})
export class BudgetAndTimelineComponent implements OnInit {
  readonly dialog = inject(MatDialog);
  @Output() changeStages: EventEmitter<any> = new EventEmitter<any>();
  @Output() changeProjectBriefViewMode: EventEmitter<VIEW_MODE> =
    new EventEmitter<VIEW_MODE>();
  @Input() projectId!: number | string | null;
  currentProgress: number;
  editProjectInfo: any;
  grade: any;
  selectedGradeName: any;
  selectedStakeholders: string[] = [];
  showMileStonesField = false;
  projectInfo = '';
  formattedValue = '';
  stakeholder: any = '';
  currencyInput = 0;
  currentImageIndex = 0;
  isFetch = false;
  form!: FormGroup;
  isLoadingNext = false;
  isLoadingSave = false;
  countryFromMap = 'US';
  countryId = 185;
  currencySymbol: any;
  currency: any = 'USD';

  capbudget: any;
  country: any;

  amountInWords: any;
  RCamountInWords: any;
  gmpAmountInWords: any;

  progress = 0;
  storedProgress = 0;
  isStageOneSendForApproval = false;
  allocatedAmount: any;
  maxBudget = 100000000000;
  approvalDetails: ApprovalDetail[] = [];

  isStageOneComplete = false;
  selectedSection = 'section1';
  stage1Progress: any;
  constructor(
    private countryServices: CountryService,
    private fb: FormBuilder,
    private projectService: ProjectsService,
    private toast: NgToastService,
    private wordConverter: WordConverterService,
    private progressService: ProgressService,
    private TabbedScrollerComponent: TabbedScrollerComponent,
    private router: Router,
    private GetProjectIdService: GetProjectIdService,
    private dialogService: DialogUtilityService
  ) {}

  ngOnInit() {
    this.form = this.progressService.getProjectBudgetTimelineForm();
    this.addRcbudgetValueChangeHandler();
    this.addAllocatedValueChangeHandler();
    this.getCurrencyInformation();
    this.projectDetail();
    this.form
      .get('mileStonesCheck')
      ?.valueChanges.subscribe((checked: boolean) => {
        this.showMileStonesField = checked;
      });
    this.fetchCurrentProgress();
    this.checkStageOneApproval();
    this.progressService.progress$.subscribe((value) => {
      this.stage1Progress = value;
    });
  }

  fetchCurrentProgress() {
    this.currentProgress = this.progressService.getProgressValue();
  }

  // TODO: we will create a global function and move it there so it can be used for all six stages
  async checkStageOneApproval(): Promise<void> {
    /**
     * send request to check if approval for stage 1 exists.
     * show component sendforapproval/project-stage-approval based on the result
     */
    const projectId = localStorage.getItem('projectId');
    if (!projectId) {
      return;
    }

    const data = {
      project_id: projectId,
      stage: 1, // as this is info component so stage will always be 1
    };

    try {
      const temp = await this.projectService
        .getCurrentStageApproval(data)
        .toPromise();

      if (temp.data.length > 0) {
        this.isStageOneSendForApproval = true;
        this.approvalDetails = temp?.data?.map((item: any) => ({
          feedback: item.feedback,
          status: item.status,
          userName: item.user.name,
          userTitle: item.user.title,
          parentStatus: item?.parent_status,
        }));
      }
    } catch (error) {
      console.log(error);
    }
  }

  private addAllocatedValueChangeHandler() {
    this.form.get('allocated')?.valueChanges.subscribe((value: number) => {
      if (value && value > 0) {
        this.form.get('showGMPFields')?.enable();
      } else {
        this.form.get('showGMPFields')?.disable();
        this.form.get('showGMPFields')?.setValue(false);

        this.form.get('rcbudget')?.setValue(null);
        this.form.get('capbudget')?.setValue(null);
      }
      this.validateBudget(value);
      this.onRcbudgetChange();
    });
  }

  // Function to validate and correct budget
  validateBudget(value: string | number): void {
    const numericValue =
      typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;

    if (numericValue > this.maxBudget) {
      this.form.controls['allocated'].setValue(this.maxBudget, {
        emitEvent: false,
      });
    }
  }

  private getCurrencyInformation() {
    this.currencySymbol = this.countryServices
      .getCountries()
      .subscribe((country: any) => {
        this.country = country;
      });

    const data: any = localStorage.getItem('selectedCountry');
    const parseData: any = JSON.parse(data);
    this.currencySymbol = parseData?.symbol || '$';
    this.currency = parseData?.currency || 'USD';
  }

  private addRcbudgetValueChangeHandler() {
    this.form.get('rcbudget')?.valueChanges.subscribe((rcValue: any) => {
      this.onRcbudgetChange();
      const rcbudget: any = rcValue;
      this.allocatedAmount = parseFloat(rcbudget); // Convert to a float

      if (rcbudget && rcbudget !== '' && isFinite(rcbudget)) {
        // Check if it's a finite number
        this.RCamountInWords = this.convertNumberToWords(rcbudget);
      } else {
        this.RCamountInWords = '';
      }
    });
  }

  get mileStonesGroups(): FormArray {
    return this.form.get('mileStonesData') as FormArray;
  }

  addQualityForm() {
    // Add a new form group with the custom date range validator
    this.mileStonesGroups.push(
      this.fb.group({
        name: [''],
        start_date: [''],
        end_date: [''],
      })
    );
  }

  removeQualityForm(index: number) {
    if (this.mileStonesGroups.length > 1) {
      this.mileStonesGroups.removeAt(index);
    }
  }

  onRcbudgetChange() {
    const rcbudget = this.form.get('rcbudget')?.value;
    const allocated = this.form.get('allocated')?.value;

    if (this.form.get('showGMPFields')?.value) {
      if (rcbudget) {
        this.capbudget = parseFloat(rcbudget) + parseFloat(allocated);
        this.allocatedAmount = parseFloat(this.capbudget); // Convert to a float

        if (isFinite(this.allocatedAmount)) {
          // Check if it's a finite number
          this.gmpAmountInWords = this.convertNumberToWords(
            this.allocatedAmount
          );
        } else {
          this.gmpAmountInWords = ''; // Clear the words if the amount is not valid
        }
      } else {
        this.capbudget = allocated;
      }
      this.form.get('capbudget')?.setValue(this.capbudget);
    } else {
      this.capbudget = allocated;
      this.form.get('capbudget')?.setValue(this.capbudget);
    }

    this.allocatedAmount = parseFloat(this.capbudget); // Convert to a float

    if (isFinite(this.allocatedAmount)) {
      // Check if it's a finite number
      this.gmpAmountInWords = this.convertNumberToWords(this.allocatedAmount);
    } else {
      this.gmpAmountInWords = ''; // Clear the words if the amount is not valid
    }

    if (isFinite(allocated)) {
      // Check if it's a finite number
      this.amountInWords = this.convertNumberToWords(allocated);
    } else {
      this.amountInWords = ''; // Clear the words if the amount is not valid
    }
  }

  projectDetail() {
    if (!this.projectId) {
      console.warn('Project not found');
    }

    this.projectService.getProjectBriefDetails(this.projectId).subscribe({
      next: (response: any) => {
        this.GetProjectIdService.setProjectId(this.projectId);
        this.editProjectInfo = response;

        if (response?.name) {
          this.form.controls['name']?.setValue(response.name);
        }
        if (response?.grade?.id) {
          this.grade = response.grade.id;
          this.selectedGradeName = response.grade.name;
        }
        if (response?.budget?.amount) {
          this.form.controls['allocated']?.setValue(response.budget.amount);
        }
        if (response?.country?.id) {
          this.country = response.country;
        }
        if (response?.milestones_enabled !== undefined) {
          this.form.controls['mileStonesCheck']?.setValue(
            response.milestones_enabled === 1
          );
        }
        this.mileStonesGroups.clear();

        // console.log('response?.mile_stones', response?.mile_stones);
        if (response?.mile_stones && response?.mile_stones.length > 0) {
          this.form.controls['mileStonesCheck']?.setValue(true);
          response.mile_stones.forEach((milestone: any) => {
            this.mileStonesGroups.push(
              this.fb.group({
                name: [milestone.name || ''],
                start_date: [milestone.start_date || ''],
                end_date: [milestone.end_date || ''],
              })
            );
          });
        } else {
          this.mileStonesGroups.push(
            this.fb.group({
              name: [''],
              start_date: [''],
              end_date: [''],
            })
          );
        }

        if (response?.budget?.gmp !== undefined) {
          this.form.controls['showGMPFields']?.setValue(
            response.budget.gmp === 1
          );
        }

        if (response?.budget?.risk_and_contengency_amount) {
          this.form.controls['rcbudget']?.setValue(
            response.budget.risk_and_contengency_amount
          );
        }

        if (response?.stake_holders) {
          const stakeholderNames = response.stake_holders.map(
            (stakeholder: any) => stakeholder.stake_holder
          );
          this.selectedStakeholders = stakeholderNames;
        }

        if (response?.start_date) {
          this.form.controls['projectStartDate']?.setValue(
            this.formatDate(response?.start_date)
          );
        }
        if (response?.end_date) {
          this.form.controls['projectEndDate']?.setValue(
            this.formatDate(response?.end_date)
          );
        }

        if (response.stage_one_completion_percentage) {
          this.setProjectStageOneProgress(
            response.stage_one_completion_percentage
          );
          this.progress = response.stage_one_completion_percentage;
          this.storedProgress = response.stage_one_completion_percentage;
        }
      },
      error: (error: any) => {
        console.error('Error fetching the project detail:', error);
      },
    });
  }

  // Helper function to format date to YYYY-MM-DD
  formatDate(dateString: string): string | null {
    if (!dateString) return null;
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = ('0' + (date.getMonth() + 1)).slice(-2); // Add leading zero
    const day = ('0' + date.getDate()).slice(-2); // Add leading zero
    return `${year}-${month}-${day}`;
  }

  preventSubmit(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault(); // Prevent form submission
    }
  }

  addStakeHolder(content: any) {
    // console.log('The stakeholder get called', this.selectedStakeholders);
    this.selectedStakeholders.push(content);
    this.progressService.updateProgressMetrics(
      'selectedStakeholders',
      this.selectedStakeholders
    );
  }
  removeStakeholder(stakeholder: any): void {
    // console.log('THe pass stakeholder is:', stakeholder);
    const index = this.selectedStakeholders.indexOf(stakeholder);
    if (index >= 0) {
      this.selectedStakeholders.splice(index, 1);
    }
    this.progressService.updateProgressMetrics(
      'selectedStakeholders',
      this.selectedStakeholders
    );
  }
  addGrade(content: any) {
    this.grade = content;
    this.progressService.updateProgressMetrics('grade', this.grade);
  }

  convertNumberToWords(amount: any): any {
    if (amount && amount !== '' && isFinite(amount)) {
      return this.wordConverter.convert(amount);
    } else {
      return '';
    }
  }

  async handleSubmit(action: string) {
    const dataToSubmit = await this.prepareDataToSubmit();
    if (!this.projectId) {
      this.toast.danger('Unprocessable data', 'DANGER', 4000);
      return;
    }
    if (action === 'next') {
      this.isLoadingNext = true;
    } else {
      this.isLoadingSave = true;
    }
    this.projectService
      .updateProjectBUdgetAndTimelineDetails(dataToSubmit, +this.projectId)
      .subscribe({
        next: (response: any) => {
          if (response.meta.success) {
            this.toast.success(
              PROJECT_MESSAGES.PROJECT_UPDATED,
              'SUCCESS',
              5000
            );
            this.processProjectAddUpdateResponse(response);
            if (action === 'next') {
              this.closeDialog();
              this.router.navigate(['/brief-plan-view']);
            }
            if (action === 'next') {
              this.isLoadingNext = false;
            } else {
              this.isLoadingSave = false;
            }
            this.setProjectStageOneProgress(
              response.data.stage_one_completion_percentage
            );
          }
        },
        error: (error: any) => {
          if (action === 'next') {
            this.isLoadingNext = false;
          } else {
            this.isLoadingSave = false;
          }

          console.error('Login failed:', error);
          this.toast.danger(error.error.message, 'DANGER', 4000);
        },
      });

    this.isFetch = true;
  }

  private processProjectAddUpdateResponse(response: any) {
    // console.log('Project added successfully:', response);

    localStorage.setItem('projectId', response.data.id);
    const stages = {
      stage1: true,
      stage2: false,
      stage3: false,
    };
    localStorage.setItem('stages', JSON.stringify(stages));
    this.changeStages.emit(stages);
    this.storedProgress = response.data.stage_one_completion_percentage;
    localStorage.setItem('isEdit', String(true));
  }

  dateFormatter = (date: any) => {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0'); // Month is 0-based
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  async prepareDataToSubmit() {
    const dataToSubmit = new FormData();
    const mileStones_Dates: any = this.form.get('mileStonesCheck')?.value
      ? 1
      : 0;

    // Append non-empty values only
    const appendIfNotEmpty = (key: string, value: any) => {
      if (value !== null && value !== undefined && value !== '') {
        dataToSubmit.append(key, value);
      }
    };

    appendIfNotEmpty('name', this.form.get('name')?.value);
    appendIfNotEmpty('grade_id', this.grade);
    appendIfNotEmpty('add_budget', this.form.get('allocated')?.value ? 1 : 0);
    appendIfNotEmpty('budget_amount', this.form.get('allocated')?.value);
    appendIfNotEmpty(
      'budget_gmp',
      this.form.get('showGMPFields')?.value ? 1 : 0
    );
    appendIfNotEmpty(
      'budget_risk_and_contengency_amount',
      this.form.get('rcbudget')?.value
    );
    appendIfNotEmpty(
      'start_date',
      this.dateFormatter(this.form.get('projectStartDate')?.value)
    );
    appendIfNotEmpty(
      'end_date',
      this.dateFormatter(this.form.get('projectEndDate')?.value)
    );

    this.selectedStakeholders.forEach((stakeholder: string) => {
      if (stakeholder) {
        dataToSubmit.append('stake_holders[]', stakeholder);
      }
    });

    appendIfNotEmpty('milestones_enabled', mileStones_Dates);

    this.mileStonesGroups.controls.forEach(
      (control: AbstractControl, index: number) => {
        const group = control as FormGroup;

        appendIfNotEmpty(
          `milestones[${index}][name]`,
          group.get('name')?.value
        );
        appendIfNotEmpty(
          `milestones[${index}][start_date]`,
          this.dateFormatter(group.get('start_date')?.value)
        );
        appendIfNotEmpty(
          `milestones[${index}][end_date]`,
          this.dateFormatter(group.get('end_date')?.value)
        );
      }
    );
    return dataToSubmit;
  }

  handleProjectStage1Approval() {
    const projectId = localStorage.getItem('projectId');
    if (!projectId) {
      this.toast.danger('Project ID not found', 'ERROR');
      return;
    }
    const dialogRef = this.dialog.open(SendForApprovalComponent, {
      width:'80vw',
      data: {
        progress: this.progress,
        stage: 1,
        projectId: projectId,
        approvalResponses: this.approvalDetails,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.success) {
        this.toast.success('Approval sent successfully!', 'SUCCESS', 4000);
        this.isStageOneSendForApproval = true;
        this.checkStageOneApproval();
        localStorage.setItem('isApproved', 'true');
      }
    });
  }

  handleBack() {
    this.TabbedScrollerComponent.previousStep();
    if (this.form.get('allocated')?.value) {
      this.handleSubmit('save');
    }
  }

  updateProgressMetrics() {
    const tagRegex = /<[^>]*>/g;
    const cleanValue = (value: string): string => {
      return value ? value.replace(tagRegex, '') : '';
    };

    const fields = ['name', 'allocated'];

    let totalFields = fields.length;
    let completedFields = 0;

    // Count fields in main form that are filled
    fields.forEach((field) => {
      if (field === 'outline' || field === 'siteconditions') {
        const cleanedValue = cleanValue(this.form.get(field)?.value);

        if (cleanedValue) {
          completedFields++;
        }
      } else {
        if (this.form.get(field)?.value) {
          completedFields++;
        }
      }
    });

    // Check mileStonesData if mileStonesCheck is true
    if (this.form.get('mileStonesCheck')?.value) {
      const mileStonesData = this.form.get('mileStonesData') as FormArray;
      totalFields += mileStonesData.length * 3; // Each group has 3 fields

      mileStonesData.controls.forEach((group) => {
        ['name', 'start_date', 'end_date'].forEach((field) => {
          if (group.get(field)?.value) {
            completedFields++;
          }
        });
      });
    }

    // Check capbudget and rcbudget if checkbox is true
    if (this.form.get('showGMPFields')?.value) {
      totalFields += 2; // Adding capbudget and rcbudget

      if (this.form.get('capbudget')?.value) {
        completedFields++;
      }
      if (this.form.get('rcbudget')?.value) {
        completedFields++;
      }
    }

    if (this.selectedStakeholders.length > 0) {
      totalFields++;
      completedFields++;
    } else {
      totalFields++;
    }
    if (this.grade) {
      totalFields++;
      completedFields++;
    } else {
      totalFields++;
    }
    if (this.countryId) {
      totalFields++;
      completedFields++;
    } else {
      totalFields++;
    }
    // Calculate progress as a percentage
    console.log(
      'budget =>> completedFields, totalFields',
      completedFields,
      totalFields
    );
    this.progress = (completedFields / totalFields) * 100;
    const progress = (completedFields / totalFields) * 100;
    this.progressService.updateProgress1(progress);
  }

  setProjectStageOneProgress(value) {
    this.isStageOneComplete = value == 100 ? true : false;
  }

  closeDialog() {
    this.dialogService.closeProjectBriefDialog();
    if (this.form.get('allocated')?.value) {
      this.handleSubmit('save');
    }
  }
}
